import React from 'react';
import { Info, ArrowRight } from '@phosphor-icons/react';

const Section = ({ title, children }) => (
  <div className="border border-gray-200 rounded-md bg-white p-6 mb-6">
    <h3 className="font-heading text-lg font-bold text-gray-900 mb-3">{title}</h3>
    <div className="text-sm text-gray-700 leading-relaxed space-y-2">{children}</div>
  </div>
);

const StatusPill = ({ label, color }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono font-medium border ${color} mr-2 mb-1`}>{label}</span>
);

export default function HelpPage() {
  return (
    <div data-testid="help-page" className="max-w-4xl">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold tracking-tighter text-gray-900">How It Works</h1>
        <p className="text-sm text-gray-500 mt-1">
          A guide to the Mayr Lead Creator workflow, statuses, and operations.
        </p>
      </div>

      <Section title="Platform Overview">
        <p>Mayr Lead Creator is an internal B2B lead generation platform developed by Mayr-Technologies. It finds companies, scores them by industry fit, collects business contacts, and pushes qualified leads into Pro-Email campaigns.</p>
        <p>The workflow is designed around controlled stages: discover, score, review, approve, and push. No leads reach your outreach without manual approval.</p>
      </Section>

      <Section title="Workflow: From Discovery to Campaign">
        <div className="space-y-3">
          {[
            { step: '1', title: 'Import or Discover Companies', desc: 'Add companies via CSV import, manual entry, or discovery jobs. The system normalizes domains and detects duplicates automatically.' },
            { step: '2', title: 'Automatic Scoring', desc: 'Every company is scored using configurable rules (industry fit, location, website presence, email quality). Scores update automatically when data changes.' },
            { step: '3', title: 'Add Contacts & Emails', desc: 'Attach contacts with business emails. Email type is classified automatically (named, generic, role-based, personal). Adding contacts recalculates the company score and readiness.' },
            { step: '4', title: 'Review & Approve', desc: 'High-scoring companies move to the review queue. Review each lead, check the score breakdown and contacts, then approve or reject.' },
            { step: '5', title: 'Export or Push to Pro-Email', desc: 'Approved leads can be exported as CSV or pushed directly to Pro-Email campaign lists.' },
          ].map(s => (
            <div key={s.step} className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-gray-900 text-white flex items-center justify-center text-xs font-mono font-bold shrink-0">{s.step}</div>
              <div>
                <p className="font-medium text-gray-900">{s.title}</p>
                <p className="text-gray-600">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Company Statuses">
        <p>Every company moves through a lifecycle. Status changes are tracked in the audit log.</p>
        <div className="mt-3 space-y-1">
          <div><StatusPill label="new" color="bg-blue-50 text-blue-700 border-blue-200" /> Freshly imported or entered. Not yet reviewed.</div>
          <div><StatusPill label="needs review" color="bg-amber-50 text-amber-700 border-amber-200" /> Auto-promoted when score + email quality meet thresholds, or manually flagged.</div>
          <div><StatusPill label="approved" color="bg-emerald-50 text-emerald-700 border-emerald-200" /> Reviewed and approved. Ready for export or Pro-Email push.</div>
          <div><StatusPill label="rejected" color="bg-red-50 text-red-700 border-red-200" /> Not a fit. Will not be exported.</div>
          <div><StatusPill label="exported" color="bg-purple-50 text-purple-700 border-purple-200" /> Included in a CSV export.</div>
          <div><StatusPill label="pushed to campaign" color="bg-green-50 text-green-800 border-green-300" /> Sent to Pro-Email. The end goal.</div>
          <div><StatusPill label="do not contact" color="bg-gray-100 text-gray-600 border-gray-300" /> Permanently blocked from outreach.</div>
        </div>
      </Section>

      <Section title="Email Classification">
        <p>Every email address is automatically classified when added:</p>
        <div className="mt-3 space-y-1">
          <div><StatusPill label="named_business" color="bg-emerald-50 text-emerald-700 border-emerald-200" /> A personal name at a business domain (e.g., john.smith@company.com). Highest value.</div>
          <div><StatusPill label="generic_business" color="bg-blue-50 text-blue-700 border-blue-200" /> A generic address at a business domain (e.g., info@, sales@). Good for initial outreach.</div>
          <div><StatusPill label="role_based" color="bg-gray-100 text-gray-600 border-gray-200" /> System addresses (admin@, webmaster@). Usually not useful for outreach.</div>
          <div><StatusPill label="personal_risky" color="bg-red-50 text-red-700 border-red-200" /> A free email provider (@gmail.com, @yahoo.com). Indicates no business domain presence.</div>
          <div><StatusPill label="invalid" color="bg-red-100 text-red-800 border-red-300" /> Failed syntax check. Cannot be used.</div>
        </div>
      </Section>

      <Section title="Email Verification Statuses">
        <div className="mt-1 space-y-1">
          <div><StatusPill label="unverified" color="bg-gray-100 text-gray-600 border-gray-200" /> No verification attempted yet.</div>
          <div><StatusPill label="syntactically_valid" color="bg-blue-50 text-blue-700 border-blue-200" /> Passes format check but not verified against mail server.</div>
          <div><StatusPill label="mx_confirmed" color="bg-emerald-50 text-emerald-700 border-emerald-200" /> Domain has valid MX records. Email likely deliverable.</div>
          <div><StatusPill label="provider_verified" color="bg-green-50 text-green-800 border-green-300" /> Confirmed by external verification service.</div>
          <div><StatusPill label="risky" color="bg-amber-50 text-amber-700 border-amber-200" /> May bounce. Use with caution.</div>
          <div><StatusPill label="catch_all" color="bg-yellow-50 text-yellow-700 border-yellow-200" /> Domain accepts all addresses. Cannot confirm specific mailbox.</div>
          <div><StatusPill label="invalid" color="bg-red-50 text-red-700 border-red-200" /> Confirmed undeliverable.</div>
        </div>
      </Section>

      <Section title="Scoring System">
        <p>Companies are scored 0-100 based on configurable rules set by admin. The score is broken into categories:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li><strong>Industry score</strong> - Does the company match target industries (AV, electrical, automation, etc.)?</li>
          <li><strong>Website score</strong> - Does the company have a live website?</li>
          <li><strong>Location score</strong> - Is the company in a target region (BC, Alberta, Spain)?</li>
          <li><strong>Contact quality score</strong> - Does the company have named business contacts?</li>
          <li><strong>Email quality score</strong> - Are emails verified and business-class?</li>
          <li><strong>Custom rules</strong> - Admin-defined rules for specific keywords or conditions.</li>
        </ul>
        <p className="mt-2">Scores recalculate automatically when contacts are added, emails are updated, or rules change. Companies scoring above the campaign-ready threshold with valid emails are auto-promoted to the review queue.</p>
      </Section>

      <Section title="Campaign Readiness">
        <p>A company is marked "campaign ready" when ALL of these are true:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Status is "approved" or "pushed to campaign"</li>
          <li>Has at least one contact with an email address</li>
          <li>Score meets or exceeds the minimum campaign-ready threshold (default: 40)</li>
        </ul>
        <p className="mt-2">Campaign readiness recalculates automatically whenever contacts, emails, scores, tags, or statuses change.</p>
      </Section>

      <Section title="Pro-Email Integration">
        <p>Pro-Email is the outreach destination for approved leads. The push flow:</p>
        <ol className="list-decimal pl-5 mt-2 space-y-1">
          <li>Filter companies to "approved" status on the Companies page</li>
          <li>Select companies using checkboxes</li>
          <li>Or use the Integrations page to push individually</li>
          <li>System selects the primary contact email for each company</li>
          <li>Leads are pushed to a named list in Pro-Email</li>
          <li>Push results are tracked in the Integration push history</li>
        </ol>
        <p className="mt-2 text-gray-500 italic">Note: Pro-Email integration is currently in mock mode. Push records are stored but no real API call is made.</p>
      </Section>

      <Section title="Common Operator Tips">
        <div className="space-y-2">
          <p><strong>Start with CSV import.</strong> It is deterministic and lets you test scoring and deduplication immediately.</p>
          <p><strong>Check scores after import.</strong> Go to a company detail page and verify the score breakdown makes sense for your rules.</p>
          <p><strong>Add contacts before approving.</strong> A company with no contacts has limited campaign value. Adding a named business email significantly boosts the score.</p>
          <p><strong>Use the review queue for fast decisions.</strong> It is designed for speed: list on left, detail on right, approve/reject with one click.</p>
          <p><strong>Export approved-only.</strong> Use the status filter on the Exports page to only export leads you have reviewed.</p>
          <p><strong>Watch the dashboard.</strong> Campaign Ready count tells you how many leads are ready to push to Pro-Email right now.</p>
        </div>
      </Section>

      <Section title="Troubleshooting">
        <div className="space-y-2">
          <p><strong>Dashboard shows "Failed to load"</strong> - Your session may have expired. Log out and log back in.</p>
          <p><strong>CSV import skipping rows</strong> - Rows are skipped if the domain already exists in the system (duplicate detection). Check the import result for details.</p>
          <p><strong>Score is 0</strong> - The company may not match any scoring rules. Check the industry field and scoring rules configuration.</p>
          <p><strong>Company not appearing in review queue</strong> - Only companies with status "new" or "needs_review" appear. Check the company status on the detail page.</p>
          <p><strong>Pro-Email push shows "mock"</strong> - The Pro-Email integration is in mock mode. Configure the API URL and token in Settings to enable live pushes.</p>
        </div>
      </Section>
    </div>
  );
}
